/**
 * Frankfurter v2 FX resolution with retries, date rollback, inverse pair, and EUR/USD pivots.
 * @see https://www.frankfurter.app/
 */

const FRANKFURTER_V2_RATES = 'https://api.frankfurter.dev/v2/rates';

const MAX_RETRIES = 4;
const INITIAL_BACKOFF_MS = 280;
const MAX_DATE_ROLLBACK_DAYS = 14;

type FrankfurterV2Row = { date: string; base: string; quote: string; rate: number };

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

function isRetryableHttpStatus(status: number): boolean {
    return status === 429 || status === 502 || status === 503 || status === 504;
}

/** UTC YYYY-MM-DD (matches Frankfurter date semantics). */
export function toUtcYmd(d: Date): string {
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/** Walk calendar days backward in UTC (handles missing publication dates). */
function* utcDatesRollingBack(from: Date, maxDays: number): Generator<string> {
    const t = Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate());
    for (let i = 0; i < maxDays; i++) {
        const d = new Date(t - i * 86400000);
        yield toUtcYmd(d);
    }
}

function isValidFxRate(r: number): boolean {
    return Number.isFinite(r) && r > 0 && r < 1e15 && r > 1e-15;
}

function toFiniteRate(value: unknown): number | null {
    if (value == null) return null;
    const r = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(r) || !isValidFxRate(r)) return null;
    return r;
}

function buildUrl(params: Record<string, string>): string {
    return `${FRANKFURTER_V2_RATES}?${new URLSearchParams(params).toString()}`;
}

/**
 * Fetch JSON array response; retries on network errors and transient HTTP failures.
 */
async function fetchV2Rows(url: string): Promise<FrankfurterV2Row[] | null> {
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
            const res = await fetch(url);
            const data: unknown = await res.json().catch(() => null);

            if (res.ok && Array.isArray(data) && data.length > 0) {
                return data as FrankfurterV2Row[];
            }

            if (!res.ok && isRetryableHttpStatus(res.status) && attempt < MAX_RETRIES - 1) {
                await sleep(INITIAL_BACKOFF_MS * 2 ** attempt + Math.random() * 120);
                continue;
            }

            return null;
        } catch {
            if (attempt < MAX_RETRIES - 1) {
                await sleep(INITIAL_BACKOFF_MS * 2 ** attempt + Math.random() * 120);
                continue;
            }
            return null;
        }
    }
    return null;
}

function firstRate(rows: FrankfurterV2Row[] | null): number | null {
    if (!rows?.length) return null;
    return toFiniteRate(rows[0]?.rate);
}

/** FROM → TO: direct quote on matching row (API may return multiple rows). */
function directMultiplier(rows: FrankfurterV2Row[] | null, from: string, to: string): number | null {
    if (!rows?.length) return null;
    const row = rows.find((r) => r.base === from && r.quote === to) ?? rows[0];
    if (row.base !== from || row.quote !== to) return null;
    return toFiniteRate(row.rate);
}

/** TO → FROM succeeded; need FROM → TO = 1 / rate. */
function inverseMultiplier(rows: FrankfurterV2Row[] | null): number | null {
    const r = firstRate(rows);
    if (r == null || r === 0) return null;
    const inv = 1 / r;
    return isValidFxRate(inv) ? inv : null;
}

/**
 * Pivot (EUR or USD): mult = (pivot→TO) / (pivot→FROM), both as “quote per 1 pivot”.
 */
async function multiplierViaPivot(dateStr: string, from: string, to: string, pivot: string): Promise<number | null> {
    if (from === pivot) {
        const rows = await fetchV2Rows(buildUrl({ base: from, quotes: to, date: dateStr }));
        return directMultiplier(rows, from, to) ?? firstRate(rows);
    }
    if (to === pivot) {
        const rows = await fetchV2Rows(buildUrl({ base: from, quotes: to, date: dateStr }));
        return directMultiplier(rows, from, to) ?? firstRate(rows);
    }

    const rows = await fetchV2Rows(buildUrl({ base: pivot, quotes: `${from},${to}`, date: dateStr }));
    if (!rows?.length) return null;

    const pivotToFrom = toFiniteRate(rows.find((r) => r.quote === from)?.rate);
    const pivotToTo = toFiniteRate(rows.find((r) => r.quote === to)?.rate);
    if (pivotToFrom == null || pivotToTo == null || pivotToFrom === 0) return null;

    const mult = pivotToTo / pivotToFrom;
    return isValidFxRate(mult) ? mult : null;
}

/**
 * Try all strategies for a single calendar date (UTC).
 */
async function multiplierForDateUtc(dateStr: string, from: string, to: string): Promise<number | null> {
    let rows: FrankfurterV2Row[] | null;

    rows = await fetchV2Rows(buildUrl({ base: from, quotes: to, date: dateStr }));
    let m = directMultiplier(rows, from, to);
    if (m != null) return m;

    rows = await fetchV2Rows(buildUrl({ base: to, quotes: from, date: dateStr }));
    m = inverseMultiplier(rows);
    if (m != null) return m;

    m = await multiplierViaPivot(dateStr, from, to, 'EUR');
    if (m != null) return m;

    m = await multiplierViaPivot(dateStr, from, to, 'USD');
    if (m != null) return m;

    return null;
}

/**
 * Resolves multiplier: amount in `to` = amount in `from` × multiplier.
 * Uses in-memory cache keyed by UTC date + pair.
 */
export async function resolveFxMultiplier(
    from: string,
    to: string,
    date: Date,
    cache: Map<string, number>
): Promise<number | null> {
    const f = from.trim().toUpperCase();
    const t = to.trim().toUpperCase();
    if (!f || !t) return null;
    if (f === t) return 1;

    const d = date instanceof Date ? date : new Date(date);
    const effective = Number.isNaN(d.getTime()) ? new Date() : d;

    const origYmd = toUtcYmd(effective);

    for (const dateStr of utcDatesRollingBack(effective, MAX_DATE_ROLLBACK_DAYS)) {
        const cacheKey = `${dateStr}|${f}|${t}`;
        const cached = cache.get(cacheKey) ?? cache.get(`${origYmd}|${f}|${t}`);
        if (cached != null) return cached;

        const mult = await multiplierForDateUtc(dateStr, f, t);
        if (mult != null) {
            cache.set(cacheKey, mult);
            cache.set(`${origYmd}|${f}|${t}`, mult);
            return mult;
        }
    }

    return null;
}
